import { setAttr } from 'redom';

import { computeEditorOptionsPosition } from 'generics/components/basic-text-editor-slate/utilities';

export function getElementCoordinates(element) { // crossbrowser version

  var box = element.getBoundingClientRect();

  var body = document.body;
  var docEl = document.documentElement;

  var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  var scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

  var clientTop = docEl.clientTop || body.clientTop || 0;
  var clientLeft = docEl.clientLeft || body.clientLeft || 0;

  var top = box.top + scrollTop - clientTop;
  var left = box.left + scrollLeft - clientLeft;

  return { top: Math.round(top), left: Math.round(left) };
};

export function getRelativeElementCoordinates(parentElement, element) { // crossbrowser version

  var box = element.getBoundingClientRect();

  var coordinates = getElementCoordinates(parentElement);

  var clientTop = coordinates.top;
  var clientLeft = coordinates.left;

  var top = box.top - clientTop;
  var left = box.left - clientLeft;

  return { top: Math.round(top), left: Math.round(left) };

};

export function computeVerticalPlacement(element, anchor, container, verticalSpacing) {

  const containerBox = container.getBoundingClientRect();
  const anchorBox = anchor.getBoundingClientRect();
  const elementBox = element.getBoundingClientRect();

  const placement = {
    top: 0,
    left: anchorBox.left
  };
  let placementTop = anchorBox.top + anchorBox.height + verticalSpacing;
  const initialElementBottom = placementTop + elementBox.height;
  const containerBottom = containerBox.top + containerBox.height;

  if (initialElementBottom <= containerBottom) {
    placement.top = placementTop;
  } else {
    placement.top = anchorBox.top - (elementBox.height + verticalSpacing);
  }

  return placement;

}

export function isElementInViewport(element) {

  var box = element.getBoundingClientRect();

  return (
    box.top >= 0 &&
    box.left >= 0 &&
    box.bottom <= (window.innerHeight || document.documentElement.clientHeight) && /* or $(window).height() */
    box.right <= (window.innerWidth || document.documentElement.clientWidth) /* or $(window).width() */
  );
}

export function displayElementBelowTarget(element, target, options = { offset: { top: 0, left: 0 } }) {

  const boundingRectangle = target.getBoundingClientRect();
  const viewportElement = document.body;

  element.style.display = 'block';
  const position = computeEditorOptionsPosition(element, boundingRectangle, viewportElement, 2);

  element.style.top = (position.top + options.offset.top) + 'px';
  element.style.left = (position.left + options.offset.left) + 'px';

}

function getLeftPosition(elementRect, anchorRect, placeOnCenter) {

  if (placeOnCenter === false) {
    return anchorRect.left;
  }

  return (anchorRect.left + (anchorRect.width / 2)) - (elementRect.width / 2);

}

function getTopPosition(elementRect, anchorRect, placeOnCenter) {

  if (placeOnCenter === false) {
    return anchorRect.top;
  }

  return (anchorRect.top + (anchorRect.height / 2)) - (elementRect.height / 2);

}

function getTopPositionCoordinates(elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter) {

  const availableVerticalSpace = anchorRect.top - viewportRect.top;
  const neededVerticalSpace = elementRect.height + verticalOffset;

  if (availableVerticalSpace < neededVerticalSpace) {
    return null;
  }

  const availableHorizontalSpace = viewportRect.width;
  const neededHorizontalSpace = elementRect.width + horizontalOffset;

  if (availableHorizontalSpace < neededHorizontalSpace) {
    return null;
  }

  const top = anchorRect.top - (neededVerticalSpace);
  let left = getLeftPosition(elementRect, anchorRect, placeOnCenter);

  const availableHorizontalSpaceFromAnchor = (viewportRect.left + viewportRect.width) - left;

  // if the element will go beyond right of the viewport, display it till the viewport
  if (neededHorizontalSpace > availableHorizontalSpaceFromAnchor) {
    left -= (neededHorizontalSpace - availableHorizontalSpaceFromAnchor);
  }

  // if the element will start before the viewport, display it from the viewport
  if (viewportRect.left > left) {
    left = viewportRect.left + horizontalOffset;
  }

  // if the element will go beyond the right of the viewport again, say, there is no space
  if ((left + elementRect.width) > (viewportRect.left + viewportRect.width)) {
    return null;
  }

  return { top, left, height: elementRect.height };

}

function getRightPositionCoordinates(elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter) {

  const availableVerticalSpace = viewportRect.height;
  const neededVerticalSpace = elementRect.height + verticalOffset;

  if (availableVerticalSpace < neededVerticalSpace) {
    return null;
  }

  const availableHorizontalSpace = (viewportRect.left + viewportRect.width) - (anchorRect.left + anchorRect.width);
  const neededHorizontalSpace = elementRect.width + horizontalOffset;

  if (availableHorizontalSpace < neededHorizontalSpace) {
    return null;
  }

  let top = getTopPosition(elementRect, anchorRect, placeOnCenter);
  let left = anchorRect.left + anchorRect.width + horizontalOffset;

  const availableVerticalSpaceFromAnchor = (viewportRect.top + viewportRect.height) - top;

  // if the element will go below the bottom, display it till the bottom
  if (neededVerticalSpace > availableVerticalSpaceFromAnchor) {
    top -= (neededVerticalSpace - availableVerticalSpaceFromAnchor);
  }

  // if the element will go above the top, display it from the top
  if (viewportRect.top > top) {
    top = viewportRect.top + verticalOffset;
  }

  // if the element will go below the bottom again, say, there is no space.
  if ((top + elementRect.height) > (viewportRect.top + viewportRect.height)) {
    return null;
  }

  return { top, left, height: elementRect.height };

}

function getBottomPositionCoordinates(elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter) {

  const availableVerticalSpace = (viewportRect.top + viewportRect.height) - (anchorRect.top + anchorRect.height);
  const neededVerticalSpace = elementRect.height + verticalOffset;

  if (availableVerticalSpace < neededVerticalSpace) {
    return null;
  }

  const availableHorizontalSpace = viewportRect.width;
  const neededHorizontalSpace = elementRect.width + horizontalOffset;

  if (availableHorizontalSpace < neededHorizontalSpace) {
    return null;
  }

  const top = anchorRect.top + anchorRect.height + verticalOffset;
  let left = getLeftPosition(elementRect, anchorRect, placeOnCenter);

  const availableHorizontalSpaceFromAnchor = (viewportRect.left + viewportRect.width) - left;

  // if the element will go beyond right of the viewport, display it till the viewport
  if (neededHorizontalSpace > availableHorizontalSpaceFromAnchor) {
    left -= (neededHorizontalSpace - availableHorizontalSpaceFromAnchor);
  }

  // if the element will start before the viewport, display it from the viewport
  if (viewportRect.left > left) {
    left = viewportRect.left + horizontalOffset;
  }

  // if the element will go beyond the right of the viewport again, say, there is no space
  if ((left + elementRect.width) > (viewportRect.left + viewportRect.width)) {
    return null;
  }

  return { top, left, height: elementRect.height };

}

function getLeftPositionCoordinates(elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter) {

  const availableVerticalSpace = viewportRect.height;
  const neededVerticalSpace = elementRect.height + verticalOffset;

  if (availableVerticalSpace < neededVerticalSpace) {
    return null;
  }

  const availableHorizontalSpace = (anchorRect.left - viewportRect.left);
  const neededHorizontalSpace = elementRect.width + horizontalOffset;

  if (availableHorizontalSpace < neededHorizontalSpace) {
    return null;
  }

  let top = getTopPosition(elementRect, anchorRect, placeOnCenter);
  let left = anchorRect.left - neededHorizontalSpace;

  const availableVerticalSpaceFromAnchor = (viewportRect.top + viewportRect.height) - top;

  // if the element will go below the bottom, display it till the bottom
  if (neededVerticalSpace > availableVerticalSpaceFromAnchor) {
    top -= (neededVerticalSpace - availableVerticalSpaceFromAnchor);
  }

  // if the element will go above the top, display it from the top
  if (viewportRect.top > top) {
    top = viewportRect.top + verticalOffset;
  }

  // if the element will go below the bottom again, say, there is no space.
  if ((top + elementRect.height) > (viewportRect.top + viewportRect.height)) {
    return null;
  }

  return { top, left, height: elementRect.height };

}

function positionElement(element, position) {

  setAttr(element, {
    style: {
      position: 'fixed',
      top: position.top + 'px',
      left: position.left + 'px',
      height: position.height + 'px'
    }
  });

}

function getOrderOfPositionChecks(startSide = 'top') {

  if (startSide === 'top') {
    return ['top', 'right', 'bottom', 'left'];
  }

  if (startSide === 'right') {
    return ['right', 'bottom', 'left', 'top'];
  }

  if (startSide === 'bottom') {
    return ['bottom', 'left', 'top', 'right'];
  }

  if (startSide === 'left') {
    return ['left', 'top', 'right', 'bottom'];
  }

  return ['top', 'right', 'bottom', 'left'];

}

const positioners = {
  top: topPositioner,
  right: rightPositioner,
  bottom: bottomPositioner,
  left: leftPositioner
};

function topPositioner(element, elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter) {

  const topPositionCoordinates = getTopPositionCoordinates(elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter);

  if (topPositionCoordinates !== null) {
    positionElement(element, topPositionCoordinates);
    return true;
  }

  return false;

}

function rightPositioner(element, elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter) {

  const rightPositionCoordinates = getRightPositionCoordinates(elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter);

  if (rightPositionCoordinates !== null) {
    positionElement(element, rightPositionCoordinates);
    return true;
  }

  return false;

}

function bottomPositioner(element, elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter) {

  const bottomPositionCoordinates = getBottomPositionCoordinates(elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter);

  if (bottomPositionCoordinates !== null) {
    positionElement(element, bottomPositionCoordinates);
    return true;
  }

  return false;

}

function leftPositioner(element, elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter) {

  const leftPositionCoordinates = getLeftPositionCoordinates(elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter);

  if (leftPositionCoordinates !== null) {
    positionElement(element, leftPositionCoordinates);
    return true;
  }

  return false;

}

export function displayElementAroundAnchor(element, anchorElement, viewportElement = document.body, verticalOffset = 0, horizontalOffset = 0, startSide, placeOnCenter = false, positionableSides) {

  const elementRect = element.getBoundingClientRect();
  const anchorRect = anchorElement.getBoundingClientRect();
  const viewportRect = viewportElement.getBoundingClientRect();

  const positionChecksOrder = positionableSides || getOrderOfPositionChecks(startSide);

  for (let side of positionChecksOrder) {

    const positioned = positioners[side](element, elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter);

    if (positioned === false) {
      // eslint-disable-next-line no-console
      console.debug(`"${side}" position is not possible. Trying next one!`);
    } else {
      return side;
    }

  }

  // eslint-disable-next-line no-console
  console.debug('Element does not fit in any side! So, not displayed!');

  return false;

}

function getCaretBoundingClientRect() {

  const selection = window.getSelection();

  if (selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);

    if (range.collapsed) {
      return range.getBoundingClientRect();
    } else {

      const collapsedRange = range.cloneRange();
      collapsedRange.collapse(false);
      return collapsedRange.getBoundingClientRect();
    }
  }

  return null;

}

export function displayElementAroundCursor(element, viewportElement = document.body, verticalOffset = 0, horizontalOffset = 0, startSide, placeOnCenter = false, positionableSides) {

  const elementRect = element.getBoundingClientRect();
  const anchorRect = getCaretBoundingClientRect();
  const viewportRect = viewportElement.getBoundingClientRect();

  if (anchorRect === null) {
    // eslint-disable-next-line no-console
    console.error('Caret position could not be determined!');
    return;
  }

  const positionChecksOrder = positionableSides || getOrderOfPositionChecks(startSide);

  for (let side of positionChecksOrder) {

    const positioned = positioners[side](element, elementRect, anchorRect, viewportRect, verticalOffset, horizontalOffset, placeOnCenter);

    if (positioned === false) {
      // eslint-disable-next-line no-console
      console.debug(`"${side}" position is not possible. Trying next one!`);
    } else {
      return side;
    }

  }

  // eslint-disable-next-line no-console
  console.debug('Element does not fit in any side! So, not displayed!');

  return false;

}

export function displayElementAroundAnchorSticky(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides, scrollableParent) {
  element.style.height = 'auto';
  const displayed = displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

  if (displayed === false) {
    return function noop() {};
  }

  function reposition() {
    element.style.height = 'auto';
    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

  }

  scrollableParent.addEventListener('scroll', reposition);

  return function uninitialize() {
    scrollableParent.removeEventListener('scroll', reposition);
  };

}

export function getOppositeSide(side) {

  if (side === 'top') {
    return 'bottom';
  }

  if (side === 'right') {
    return 'left';
  }

  if (side === 'bottom') {
    return 'top';
  }

  if (side === 'left') {
    return 'right';
  }

  return null;

}